import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';

import { FormattedMessage, useIntl } from 'react-intl';

import { Button, ButtonSizes } from 'ui-components/components/button';
import { Card } from 'ui-components/components/card';
import { Divider } from 'ui-components/components/divider';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import mapImage from './assets/map.png';
import { BenefitsGrid } from './components/benefits-grid';
import { ProcessStep } from './components/process-step';
import { ProgressSection } from './components/progress-section';
// @ts-expect-error png import
import { ZoneComparisonSection } from './components/zone-comparison-section/zone-comparison-section';
import { FbsApplicationDrawer } from './fbs-application-drawer/fbs-application-drawer';
import { FulfilledByShipstationFTUWizard } from './fulfilled-by-shipstation-ftu-wizard';
import { useFulfillmentFTUWizardState } from './fulfilled-by-shipstation-ftu-wizard/use-fulfillment-ftu-wizard-state';
import { messages } from './messages';
import style from './style.module.less';
import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { useEnableFbsZoneComparisonSection } from '../../../feature-flags/hooks/use-enable-fbs-zone-comparison-section';
import { useModal } from '../../../hooks/use-modal';
import { useSellerSetting } from '../../../hooks/use-seller-setting';
// @ts-expect-error less import
import { useSellerShipStationFulfillmentProvider } from '../../../hooks/use-shipstation-fulfillment-whitelabel-enabled';

const REQUEST_CONSULT_LINK = 'https://calendly.com/davis-saltzgiver/fulfilledbyshipstationconsult';

export const SetupPage = () => {
  const intl = useIntl();
  const { open: openApplicationDrawer, isOpen: isApplicationDrawerOpen, close: closeApplicationDrawer } = useModal();
  const { open: openFTUWizard, isOpen: isFTUWizardOpen, close: closeFTUWizard } = useModal();

  const isSetupActivated = useSellerShipStationFulfillmentProvider();
  const { userProgress } = useFulfillmentFTUWizardState();

  const isSetupInProgress = userProgress.hasStarted && !userProgress.isComplete;
  const setupButtonMessage = isSetupInProgress ? messages.continueSetupButton : messages.startSetupButton;
  const fbsApplicationSubmittedDate = useSellerSetting(SellerSettings.fbsApplicationDate);
  const hasSubmittedApplication = Boolean(fbsApplicationSubmittedDate);
  const isZoneComparisonSectionEnabled = useEnableFbsZoneComparisonSection();

  const fbsCoverageMapImage = <img alt={intl.formatMessage(messages.imageAlt)} src={mapImage} />;

  return (
    <>
      <FlexColumn className={style['page-content']}>
        {isZoneComparisonSectionEnabled ? (
          <Card className={style['zone-comparison-map-card']}>
            <ZoneComparisonSection fallbackImage={fbsCoverageMapImage} />
            {isSetupActivated ? (
              <Button onClick={openFTUWizard} theme={THEME_CONSTANTS.primary}>
                <FormattedMessage {...setupButtonMessage} />
              </Button>
            ) : (
              <div className={style['apply-buttons']}>
                <Button
                  buttonSize={ButtonSizes.large}
                  className={style['apply-now-button']}
                  disabled={hasSubmittedApplication}
                  onClick={openApplicationDrawer}
                  theme={THEME_CONSTANTS.primary}
                >
                  <FormattedMessage {...messages.applyNowButton} />
                </Button>
                {hasSubmittedApplication ? (
                  <Button onClick={openApplicationDrawer} theme={THEME_CONSTANTS.ghost}>
                    <FormattedMessage {...messages.submitNewApplication} />
                  </Button>
                ) : null}
              </div>
            )}
            <div>
              <Divider className={style.divider} />
              <BenefitsGrid />
            </div>
          </Card>
        ) : (
          <Card>
            <FlexRow className={style['map-row']} gap={Gap.Space24}>
              <FlexColumn gap={Gap.Space16}>
                <div className={style['map-text-container']}>
                  <Text textType={TextType.H3} value={messages.subTitle} />
                  <Text className={style['map-description']} textType={TextType.H1} value={messages.valueProposition} />
                  {isSetupActivated ? (
                    <Button className={style['map-quote-button']} onClick={openFTUWizard} theme={THEME_CONSTANTS.primary}>
                      <FormattedMessage {...setupButtonMessage} />
                    </Button>
                  ) : (
                    <FlexRow gap={Gap.Space8}>
                      <Button
                        className={style['map-quote-button']}
                        disabled={hasSubmittedApplication}
                        onClick={openApplicationDrawer}
                        theme={THEME_CONSTANTS.primary}
                      >
                        <FormattedMessage {...messages.applyNowButton} />
                      </Button>
                      {hasSubmittedApplication ? (
                        <Button className={style['map-quote-button']} onClick={openApplicationDrawer} theme={THEME_CONSTANTS.ghost}>
                          <FormattedMessage {...messages.submitNewApplication} />
                        </Button>
                      ) : null}
                    </FlexRow>
                  )}
                </div>
              </FlexColumn>
              <div>{fbsCoverageMapImage}</div>
            </FlexRow>
            {isSetupInProgress ? <ProgressSection /> : null}
            <div className={style['benefits-section']}>
              <BenefitsGrid />
            </div>
          </Card>
        )}
        <div className={style['process-section']}>
          <div className={style['process-title']}>
            <Text textType={TextType.H2} value={messages.explanationTitle} />
          </div>
          <div className={style['process-steps-row']}>
            <ProcessStep description={messages.processStep1Description} stepNumber={1} title={messages.processStep1Title} />
            <ProcessStep description={messages.processStep2Description} stepNumber={2} title={messages.processStep2Title} />
            <ProcessStep description={messages.processStep3Description} stepNumber={3} title={messages.processStep3Title} />
          </div>
        </div>
        <FlexColumn className={style.footer} gap={Gap.Space24}>
          <Text textType={TextType.H2} value={messages.footerTitle} />
          <FlexRow className={style['footer-buttons']} gap={Gap.Space16}>
            {isSetupActivated ? (
              <Button className={style['footer-setup-button']} onClick={openFTUWizard} theme={THEME_CONSTANTS.secondary}>
                <FormattedMessage {...setupButtonMessage} />
              </Button>
            ) : (
              <Button className={style['footer-setup-button']} onClick={openApplicationDrawer} theme={THEME_CONSTANTS.secondary}>
                <FormattedMessage {...messages.applyNowButton} />
              </Button>
            )}
            <Button className={style['footer-learn-more-button']} href={REQUEST_CONSULT_LINK} theme={THEME_CONSTANTS.link}>
              <FormattedMessage {...messages.learnMoreButton} />
              <FontAwesomeIcon className={style['arrow-icon']} icon={faArrowRight} />
            </Button>
          </FlexRow>
        </FlexColumn>
      </FlexColumn>
      {isApplicationDrawerOpen ? <FbsApplicationDrawer onClose={closeApplicationDrawer} /> : null}
      {isFTUWizardOpen ? <FulfilledByShipstationFTUWizard onClose={closeFTUWizard} /> : null}
    </>
  );
};
